import { LayerKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useNavigationParams } from '../../system/useNavigationParams'
import type {
  TemplateFieldReference,
  TemplateReferenceNode,
  TemplateReferenceNodeFull,
  TemplateVersionInfo,
  TemplateVersionReferenceNodeFull,
} from '../../types/templateModel'
import { FALLBACK_BLOCK } from '../api/metaModel/useFetchBlocks'
import { stringToDateTimeNullSafe } from '../helpers'
import { useVersionInfosAt } from '../versionInfo/useTemplateVersionInfo'
import { useTemplateReferenceHierarchy } from './useTemplateReferenceHierarchy'

export const useTemplateReferenceNode = ({
  typeName,
  at,
  id,
}: {
  typeName: string | undefined
  at: string
  id: string
}) => {
  const { layer, block } = useNavigationParams()
  const versions = useVersionInfosAt(at)
  const hierarchy = useTemplateReferenceHierarchy({
    rootTypeName: typeName,
    layer,
    block,
  })

  return useMemo(() => {
    if (versions?.size && id && hierarchy?.size) {
      return flattenAndSimplifyAndVisitDependents({
        id,
        at,
        versions,
        hierarchy,
        resultMap: new Map(),
      })
    }

    return undefined
  }, [at, id, hierarchy, versions])
}

const flattenAndSimplifyAndVisitDependents = ({
  id,
  at,
  versions,
  hierarchy,
  resultMap,
}: {
  id: string
  at: string
  versions: Map<string, Optional<TemplateVersionInfo>>
  hierarchy: Map<string, TemplateReferenceNodeFull>
  resultMap: Map<string, TemplateReferenceNode>
}) => {
  //The general idea is that we only go upwards and forwards in the graph, so we do not visit a node while it has been cached but not fully resolved
  const node = flattenAndSimplify({ id, at, versions, hierarchy, resultMap })
  const originalNode = hierarchy.get(id) as TemplateReferenceNodeFull
  //Once we are sure that all the nodes are fully resolved in regard to flattening, we go backwards
  originalNode?.referencedBy.forEach((rb) =>
    flattenAndSimplifyAndVisitDependents({
      id: rb.owningVersion.owningNode.id,
      at,
      versions,
      hierarchy,
      resultMap,
    }),
  )

  return node
}

const flattenAndSimplify = ({
  id,
  at,
  versions,
  hierarchy,
  resultMap,
}: {
  id: string
  at: string
  versions: Map<string, Optional<TemplateVersionInfo>>
  hierarchy: Map<string, TemplateReferenceNodeFull>
  resultMap: Map<string, TemplateReferenceNode>
}): TemplateReferenceNode => {
  const resultNode = resultMap.get(id)

  if (resultNode) {
    return resultNode
  }

  const originalNode = hierarchy.get(id) as Optional<TemplateReferenceNodeFull>
  const newNode: TemplateReferenceNode = {
    id,
    typeName: originalNode?.typeName ?? '',
    type: originalNode?.type,
    referencedBy: [],
    fields: [],
    layer:
      originalNode?.layer ?? new LayerKey({ layerId: null, tenantId: null }),
    block: originalNode?.block ?? FALLBACK_BLOCK.name,
  }

  resultMap.set(id, newNode)

  newNode.fields = flattenReferences({
    id,
    at,
    newNode,
    versions,
    hierarchy,
    resultMap,
  })

  const versionInfo = versions.get(id)

  if (versionInfo) {
    //Run to children, as they might update based on the new flattening information
    versionInfo.childrenIds.forEach((c) =>
      flattenAndSimplify({ id: c, at, versions, hierarchy, resultMap }),
    )
  }

  return newNode
}

const firstActiveVersion = (
  versions: TemplateVersionReferenceNodeFull[],
  at: string,
): TemplateVersionReferenceNodeFull => {
  const atDate = stringToDateTimeNullSafe(at, Number.POSITIVE_INFINITY)
  const dateVersions = versions.map((v) => ({
    date: v.activeInterval.from,
    version: v,
  }))

  const result = dateVersions
    .sort((a, b) => a.date - b.date)
    .findLast((v) => v.date <= atDate)?.version

  assertNotEmpty(result)

  return result
}

const flattenReferences = ({
  id,
  at,
  newNode,
  versions,
  hierarchy,
  resultMap,
}: {
  id: string
  at: string
  newNode: TemplateReferenceNode
  versions: Map<string, Optional<TemplateVersionInfo>>
  hierarchy: Map<string, TemplateReferenceNodeFull>
  resultMap: Map<string, TemplateReferenceNode>
}): TemplateFieldReference[] => {
  const originalNode = hierarchy.get(id)
  const versionInfo = versions.get(id)

  if (originalNode && versionInfo) {
    const version = firstActiveVersion(originalNode.versions, at)
    const parent = versionInfo.parentId
      ? flattenAndSimplify({
          id: versionInfo.parentId,
          at,
          versions,
          hierarchy,
          resultMap,
        })
      : null

    if (!version) {
      return []
    }

    const versionFields = version.fields.map(
      (f) =>
        ({
          fieldName: f.fieldName,
          referencedTypeName: f.referencedTypeName,
          referencedType: f.referencedType,
          owningNode: newNode,
          referencedNodes: f.referencedNodes.map(
            (rn) =>
              flattenAndSimplify({
                id: rn.id,
                at,
                versions,
                hierarchy,
                resultMap,
              }), //Run to references
          ),
        }) as TemplateFieldReference,
    )

    const parentReferences = (parent?.fields ?? []).map((e) => ({
      ...e,
      owningNode: newNode,
    }))

    const returnValue = versionFields.concat(parentReferences)

    returnValue.forEach((f) => {
      f.referencedNodes.forEach(
        (rn) => (rn.referencedBy = [...rn.referencedBy, f]),
      )
    })

    return returnValue
  }

  return []
}
